import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs';
import {
    BuildQueryString,
    EventNode,
    GaugeMeasure,
    GaugeNode,
    LatLng,
    RaainApiGaugesFindAllMineRequest,
    RaainApiGaugesFindAllMineResponse,
    RaainApiGaugesFindMeasuresRequest,
    RaainApiGaugesFindMeasuresResponse,
    RaainApiGaugesFindOneResponse,
    RaainApiNotificationsAddRequestBody,
    RaainApiNotificationsAddResponse,
    RaainApiNotificationsFindAllMineRequest,
    RaainApiNotificationsFindAllMineResponse,
    RaainApiRadarsFindAllMineRequest,
    RaainApiRadarsFindAllMineResponse,
    RaainApiRadarsFindOneResponse,
    RaainApiRadarsUpdateRequestBody,
    RaainApiRainsCumulativeCartesianMapRequest,
    RaainApiRainsCumulativeCartesianMapResponse,
    RaainApiRainsCumulativeComparesRequest,
    RaainApiRainsCumulativeComparesResponse,
    RaainApiRainsCumulativeCumulativeResponse,
    RaainApiRainsCumulativeCumulativesComparesRequest,
    RaainApiRainsCumulativeCumulativesComparesResponse,
    RaainApiRainsFindAllMineRequest,
    RaainApiRainsFindAllMineResponse,
    RaainApiRainsFindOneResponse,
    RaainApiRainsFindOneTimeframeCumulativeRequest,
    RaainApiRainsFindOneTimeframeCumulativeResponse,
    RaainApiRainsGetCountsRequest,
    RaainApiRainsGetCountsResponse,
    RaainApiRainsIndicatorsRequest,
    RaainApiRainsIndicatorsResponse,
    RaainApiRainsProgressResponse,
    RaainApiRainsCumulativesListRequest,
    RaainApiRainsCumulativesListResponse,
    RaainApiRainsCumulativesDetailedResponse,
    RaainApiRainsCumulativesCreateRequest,
    RaainApiRainsCumulativesCreateResponse,
    RaainApiRainsProvidersResponse,
    RaainApiTeamsFindAllMineRequest,
    RaainApiTeamsFindOneResponse,
    RadarNode,
    RainComputationMap,
    RainComputationQuality,
    RainNode,
    SpeedMatrixContainer,
    TeamNode,
} from 'raain-model';

import {Storage} from './storage.service';

import {FidjStorage, FidjStorageNode} from './fidj-storage.model';
import {ErrorInterface, FidjService, LoggerLevelEnum} from 'fidj-angular';
import {XYType} from './xytype';
import {GaugeNodeFilter} from './tools';
import {Router} from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class ProfileService {
    public readyForSync: BehaviorSubject<boolean>;
    public asTeamId: string;

    protected email: string;
    protected isDemo: boolean;
    protected roles: Array<string>;
    protected nodeData: FidjStorageNode;
    protected fidjStorage: FidjStorage;
    protected haveTryToReconnect: boolean;

    constructor(
        protected storage: Storage,
        protected fidjService: FidjService,
        protected httpClient: HttpClient,
        private router: Router
    ) {
        this.email = this.storage.get('raain-email');
        this.asTeamId = this.storage.get('raain-asTeamId');
        this.readyForSync = new BehaviorSubject<boolean>(false);
        this.roles = [];
        this.fidjStorage = new FidjStorage(storage);
        this.isDemoMode = false;
    }

    get isDemoMode() {
        return this.isDemo;
    }

    set isDemoMode(mode) {
        this.isDemo = mode ? mode : true;
        this.fidjStorage.setDemoMode(this.isDemo);
    }

    get defaultUrlForAPI() {
        return this.storage.get('raain-urlForAPI');
    }

    set defaultUrlForAPI(url: string) {
        this.storage.set('raain-urlForAPI', url);
    }

    async refreshProfile(): Promise<FidjStorageNode> {
        try {
            this.nodeData = await this.fidjStorage.getRefreshedNodeCopy(this.fidjService);
            this.setRoles(await this.fidjService.getRoles());
            return this.nodeData;
        } catch (e) {
            await this.checkError(e);
        }
    }

    async isConnected() {
        return this.fidjService.isConnected();
    }

    getEmail() {
        return this.email ?? this.storage.get('raain-email', this.email);
    }

    setEmail(email: string) {
        this.email = email;
        this.storage.set('raain-email', this.email);
    }

    async logout(fidjKey: string, fidjProd: boolean) {
        // this.storage.remove('raain-email');

        if (!fidjKey) {
            try {
                await this.fidjService.loginInDemoMode();
                this.readyForSync.next(true);
            } catch (err) {
                console.error('initFidj catch pb: ', err);
            }

            return;
        }

        await this.fidjService.logout(true);

        try {
            await this.fidjService.init(fidjKey, {
                logLevel: LoggerLevelEnum.WARN, // fidjProd ? LoggerLevelEnum.WARN : LoggerLevelEnum.INFO,
                crypto: false,
                prod: fidjProd,
                useDB: false,
            });
            this.readyForSync.next(true);
        } catch (err) {
            console.error('initFidj catch pb: ', err);
        }
    }

    async checkError(error: ErrorInterface) {
        if (error.code === 401) {
            console.warn('Pb on auth');
            if (this.router.url.indexOf('login') < 0) {
                try {
                    await this.fidjService.logout();
                } catch (ignored) {
                    // Ignore logout errors as we're redirecting to logout page anyway
                }
                return this.gotoLout();
            }
        }
    }

    async gotoLout() {
        try {
            if (this.router.url.indexOf('login') > -1) {
                return;
            }
            await this.router.navigateByUrl('/logout', {
                skipLocationChange: true,
                replaceUrl: true,
            });
        } catch (e) {
            console.error('gotoLout error: ', e);
        }
    }

    async gotoLogin() {
        if (this.router.url.indexOf('login') > -1) {
            return;
        }
        // await this.router.navigateByUrl('/', {skipLocationChange: true});
        await this.router.navigate(['/login']);
    }

    isLoggedIn() {
        const loggedIn = this.fidjService.isLoggedIn();
        console.log('isLoggedIn: ', loggedIn);
        return loggedIn;
    }

    needsConnectionRefresh() {
        return this.fidjService.needsRefresh();
    }

    async connectionRefresh() {
        await this.fidjService.refresh();
    }

    async storeAll() {
        return this.fidjStorage.storeData(this.fidjService, this.nodeData);
    }

    isAdmin(): boolean {
        return this.roles.indexOf('admin') > -1;
    }

    // === Notifications ===

    async createNotification(rainId: string, message: string) {
        const data: RaainApiNotificationsAddRequestBody = {
            rain: rainId,
            message,
        };

        try {
            const resp = await this.fidjService.sendOnEndpoint<
                RaainApiNotificationsAddRequestBody,
                RaainApiNotificationsAddResponse
            >({
                key: 'notifications',
                verb: 'POST',
                defaultKeyUrl: this.defaultUrlForAPI + '/notifications',
                data,
            });
            return new EventNode(resp.data);
        } catch (e) {
            await this.checkError(e);
        }

        return null;
    }

    async getNotifications(rainId: string) {
        try {
            type NotificationsFindParams = Partial<RaainApiNotificationsFindAllMineRequest>;
            const params: NotificationsFindParams = {rain: rainId};
            const queryString = BuildQueryString<NotificationsFindParams>(params);
            const resp = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiNotificationsFindAllMineResponse
            >({
                key: 'notifications',
                verb: 'GET',
                relativePath: queryString ? '?' + queryString : '',
                defaultKeyUrl: this.defaultUrlForAPI + '/notifications',
            });

            return resp.data.notifications.map((n) => new EventNode(n));
        } catch (e) {
            await this.checkError(e);
        }

        return null;
    }

    async getAllNotifications(): Promise<EventNode[]> {
        try {
            const resp = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiNotificationsFindAllMineResponse
            >({
                key: 'notifications',
                verb: 'GET',
                defaultKeyUrl: this.defaultUrlForAPI + '/notifications',
            });

            return resp.data.notifications.map((n) => new EventNode(n));
        } catch (e) {
            await this.checkError(e);
        }

        return [];
    }

    // === Teams ===

    async getTeams(): Promise<Array<TeamNode>> {
        const teams = [];
        const params = {};
        const queryString = BuildQueryString<RaainApiTeamsFindAllMineRequest>(params);
        try {
            const resp = await this.fidjService.sendOnEndpoint<
                void,
                {teams: ReturnType<TeamNode['toJSON']>[]}
            >({
                key: 'teams',
                verb: 'GET',
                relativePath: queryString ? '?' + queryString : '',
                defaultKeyUrl: this.defaultUrlForAPI + '/teams',
            });
            for (const team of resp.data.teams) {
                teams.push(new TeamNode(team));
            }
        } catch (e) {
            await this.checkError(e);
        }
        return teams;
    }

    async getTeam(teamId: string): Promise<TeamNode> {
        try {
            const resp = await this.fidjService.sendOnEndpoint<void, RaainApiTeamsFindOneResponse>({
                key: 'teams',
                verb: 'GET',
                relativePath: teamId,
                defaultKeyUrl: this.defaultUrlForAPI + '/teams',
            });
            return new TeamNode(resp.data);
        } catch (e) {
            await this.checkError(e);
        }

        return null;
    }

    // === Radars ===

    async getRadars(name?: string): Promise<Array<RadarNode>> {
        const radars = [];
        const params: RaainApiRadarsFindAllMineRequest = {};
        if (name) {
            params.name = name;
        }
        const queryString = BuildQueryString<RaainApiRadarsFindAllMineRequest>(params);
        try {
            const resp = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRadarsFindAllMineResponse
            >({
                key: 'radars',
                verb: 'GET',
                relativePath: queryString ? '?' + queryString : '',
                defaultKeyUrl: this.defaultUrlForAPI + '/radars',
            });
            for (const r of resp.data.radars) {
                const radar = new RadarNode(r);
                radars.push(radar);
            }
        } catch (e) {
            await this.checkError(e);
        }
        return radars;
    }

    async getRadar(id: string): Promise<RadarNode> {
        try {
            const resp = await this.fidjService.sendOnEndpoint<void, RaainApiRadarsFindOneResponse>(
                {
                    key: 'radars',
                    verb: 'GET',
                    relativePath: id,
                    defaultKeyUrl: this.defaultUrlForAPI + '/radars',
                }
            );
            return new RadarNode(resp.data);
        } catch (e) {
            await this.checkError(e);
        }

        return null;
    }

    async putRadar(radarNode: RadarNode): Promise<RadarNode> {
        const data: RaainApiRadarsUpdateRequestBody = {
            name: radarNode.name,
        };

        try {
            const resp = await this.fidjService.sendOnEndpoint<
                RaainApiRadarsUpdateRequestBody,
                ReturnType<RadarNode['toJSON']>
            >({
                key: 'radars',
                relativePath: radarNode.id,
                verb: 'PUT',
                data,
                defaultKeyUrl: this.defaultUrlForAPI + '/radars/',
            });

            return new RadarNode(resp.data);
        } catch (e) {
            await this.checkError(e);
        }
    }

    async getLonelyRadars(rains: RainNode[]): Promise<RadarNode[]> {
        try {
            const resp = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRadarsFindAllMineResponse
            >({
                key: 'radars',
                verb: 'GET',
                defaultKeyUrl: this.defaultUrlForAPI + '/radars',
            });
            const lonelyRadars: RadarNode[] = [];
            const radars = resp.data.radars;
            radars.forEach((radar) => {
                let found = false;
                rains.forEach((rain) => {
                    const rdId = rain.getLink(RadarNode.TYPE).getId();
                    if (rdId === radar.id) {
                        found = true;
                    }
                });
                if (!found) {
                    lonelyRadars.push(new RadarNode(radar));
                }
            });
            return lonelyRadars;
        } catch (e) {
            await this.checkError(e);
        }
        return [];
    }

    async getRainTimeframe(
        rainId: string,
        begin: Date,
        end: Date,
        forced = false,
        provider = 'Raain',
        timeStepInMinutes = 10,
        windowInMinutes = 0
    ): Promise<RainNode> {
        try {
            const params: RaainApiRainsFindOneTimeframeCumulativeRequest = {
                format: 'timeframeCumulative',
                provider,
                timeStepInMinutes: String(timeStepInMinutes),
                begin: begin?.toISOString(),
                end: end?.toISOString(),
            };
            if (forced) {
                params.forced = 'true';
            }
            params.windowInMinutes = String(windowInMinutes);
            const queryString =
                BuildQueryString<RaainApiRainsFindOneTimeframeCumulativeRequest>(params);
            const resp = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsFindOneTimeframeCumulativeResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: rainId + (queryString ? '?' + queryString : ''),
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });
            const rainNode = new RainNode(resp.data.timeframeCumulative);
            rainNode.name += '.radar.timeframeCumulative';
            return rainNode;
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    // === Rains ===

    async getRains(name?: string): Promise<Array<RainNode>> {
        const rains = [];
        const params: RaainApiRainsFindAllMineRequest = {};
        if (name) {
            params.name = name;
        }
        const queryString = BuildQueryString<RaainApiRainsFindAllMineRequest>(params);
        try {
            const resp = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsFindAllMineResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: queryString ? '?' + queryString : '',
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });
            for (const rain of resp.data.rains) {
                rains.push(new RainNode(rain));
            }
        } catch (e) {
            await this.checkError(e);
        }
        return rains;
    }

    async getRain(id: string): Promise<RainNode> {
        try {
            const resp = await this.fidjService.sendOnEndpoint<void, RaainApiRainsFindOneResponse>({
                key: 'rains',
                relativePath: id,
                verb: 'GET',
                defaultKeyUrl: this.defaultUrlForAPI + '/rains/',
            });
            return new RainNode(resp.data);
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    // === Count

    async getCounts(
        rainId: string,
        options: {
            range: 'hour' | 'day' | 'month' | 'year';
            periodBegin: Date;
        }
    ): Promise<{
        percentImages: XYType[];
        percentRainy: XYType[];
        percentQ: XYType[];
        queueRunning: number;
    }> {
        try {
            const params: RaainApiRainsGetCountsRequest = {
                range: options.range,
                begin: options.periodBegin.toISOString(),
            };
            const queryString = BuildQueryString<RaainApiRainsGetCountsRequest>(params);

            const resp = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsGetCountsResponse
            >({
                key: 'rains',
                relativePath: rainId + '/counts' + (queryString ? '?' + queryString : ''),
                verb: 'GET',
                defaultKeyUrl: this.defaultUrlForAPI + '/rains/',
            });

            const counts = resp.data.counts.result;
            const percentImages: XYType[] = [],
                percentRainy: XYType[] = [],
                percentQ: XYType[] = [];

            counts.forEach((c) => {
                const label = this.setDateComponents(options.periodBegin, c);
                percentImages.push(new XYType(c.percentImages ?? 0, NaN, NaN, label));
                percentRainy.push(new XYType(c.percentRainy ?? 0, NaN, NaN, label));
                percentQ.push(new XYType(c.percentQ ?? 0, NaN, NaN, label));
            });

            return {
                percentImages,
                percentRainy,
                percentQ,
                queueRunning: resp.data.queueRunning,
            };
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    async getCountsHour(
        rainId: string,
        options: {periodBegin: Date}
    ): Promise<{
        percentImages: XYType[];
        rainyCount: XYType[];
        rainySum: XYType[];
        queueRunning: number;
    }> {
        try {
            const params: RaainApiRainsGetCountsRequest = {
                range: 'hour',
                begin: options.periodBegin.toISOString(),
            };
            const queryString = BuildQueryString<RaainApiRainsGetCountsRequest>(params);

            const resp = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsGetCountsResponse
            >({
                key: 'rains',
                relativePath: rainId + '/counts' + (queryString ? '?' + queryString : ''),
                verb: 'GET',
                defaultKeyUrl: this.defaultUrlForAPI + '/rains/',
            });

            const counts = resp.data.counts.result;
            const percentImages: XYType[] = [],
                rainySum: XYType[] = [],
                rainyCount: XYType[] = [];

            counts.forEach((c) => {
                const label = this.setDateComponents(options.periodBegin, c);
                percentImages.push(new XYType(c.percentImages ?? 0, NaN, NaN, label));
                rainyCount.push(new XYType(c.rainyCount ?? 0, NaN, NaN, label));
                rainySum.push(new XYType(c.rainySum ?? 0, NaN, NaN, label));
            });

            return {
                percentImages,
                rainyCount,
                rainySum,
                queueRunning: resp.data.queueRunning,
            };
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    // === Computing ===

    async getRainComputationCumulativeCartesianMapById(
        rainId: string,
        rainComputationCumulativeId: string
    ): Promise<RainComputationMap> {
        const params: RaainApiRainsCumulativeCartesianMapRequest = {format: 'cartesian-map'};
        const queryString = BuildQueryString<RaainApiRainsCumulativeCartesianMapRequest>(params);

        try {
            const response = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsCumulativeCartesianMapResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: `${rainId}/cumulatives/${rainComputationCumulativeId}?${queryString}`,
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });
            if (!response.data['cartesian-map']) {
                return null;
            }

            const rainComputationMap = new RainComputationMap(response.data['cartesian-map']);
            rainComputationMap.name = rainId + '.rain.cartesian-map';
            return rainComputationMap;
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    async getRainComputationCumulativeCumulativesMapById(
        rainId: string,
        rainComputationCumulativeId: string,
        cumulativeHours: number
    ): Promise<RainComputationMap> {
        const queryPath = `${rainId}/cumulatives/${rainComputationCumulativeId}/cumulative/${cumulativeHours}`;

        try {
            const response = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsCumulativeCumulativeResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: queryPath,
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });
            if (!response.data['cumulative']) {
                return null;
            }

            const rainComputationMap = new RainComputationMap(response.data['cumulative']);
            rainComputationMap.name = rainId + '.rain.cumulative-cumulative';
            return rainComputationMap;
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    async getRainCumulativeCompareByDate(
        rainNode: RainNode,
        rainComputationCumulativeId: string,
        date: Date
    ): Promise<RainComputationQuality> {
        const params: RaainApiRainsCumulativeComparesRequest = {date: date.toISOString()};
        const queryString = BuildQueryString<RaainApiRainsCumulativeComparesRequest>(params);

        try {
            const response = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsCumulativeComparesResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: `${rainNode.id}/cumulatives/${rainComputationCumulativeId}/compares?${queryString}`,
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });

            const qualityJson = response.data.qualities[0];
            const rainComputationQuality = new RainComputationQuality(qualityJson);
            rainComputationQuality.qualitySpeedMatrixContainer =
                SpeedMatrixContainer.CreateFromJson(
                    rainComputationQuality.qualitySpeedMatrixContainer
                );
            return rainComputationQuality;
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    async getRainCumulativeCumulativesCompareByDate(
        rainNode: RainNode,
        rainComputationCumulativeId: string,
        date: Date,
        cumulativeHours: number
    ): Promise<RainComputationQuality> {
        const params: RaainApiRainsCumulativeCumulativesComparesRequest = {
            date: date.toISOString(),
            cumulativeHours,
        };
        const queryString =
            BuildQueryString<RaainApiRainsCumulativeCumulativesComparesRequest>(params);

        try {
            const response = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsCumulativeCumulativesComparesResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: `${rainNode.id}/cumulatives/${rainComputationCumulativeId}/compares?${queryString}`,
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });

            const qualityJson = response.data.qualities[0];
            const rainComputationQuality = new RainComputationQuality(qualityJson);
            rainComputationQuality.qualitySpeedMatrixContainer =
                SpeedMatrixContainer.CreateFromJson(
                    rainComputationQuality.qualitySpeedMatrixContainer
                );
            return rainComputationQuality;
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    async getRainProgress(rainId: string): Promise<number> {
        try {
            const queryPath = '' + rainId + '/progress';
            const response = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsProgressResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: queryPath,
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });

            // return response.data.inProgress;
            return response.data.inQueue;
        } catch (e) {
            await this.checkError(e);
        }
        return 0;
    }

    async getIndicators(rainId: string): Promise<RaainApiRainsIndicatorsResponse> {
        try {
            const params: RaainApiRainsIndicatorsRequest = {
                cumulative: 'true',
            };
            const queryString = BuildQueryString<RaainApiRainsIndicatorsRequest>(params);
            const response = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsIndicatorsResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: rainId + '/indicators' + (queryString ? '?' + queryString : ''),
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });

            return response.data;
        } catch (e) {
            await this.checkError(e);
        }
        return {indicators: []};
    }

    // GET /rains/:rainId/cumulatives - List available cumulative periods
    // With detailed=true, returns individual cumulatives instead of aggregated periods
    async getCumulativePeriods(
        rainId: string,
        filters?: {
            windowInMinutes?: number;
            provider?: string;
            isReady?: boolean;
            forced?: boolean;
            detailed?: boolean;
            page?: number;
            limit?: number;
        }
    ): Promise<RaainApiRainsCumulativesListResponse | RaainApiRainsCumulativesDetailedResponse> {
        try {
            const params: RaainApiRainsCumulativesListRequest = {};
            if (filters?.windowInMinutes !== undefined) {
                params.windowInMinutes = filters.windowInMinutes;
            }
            if (filters?.provider) {
                params.provider = filters.provider;
            }
            if (filters?.isReady !== undefined) {
                params.isReady = filters.isReady;
            }
            if (filters?.forced !== undefined) {
                params.forced = filters.forced;
            }
            if (filters?.detailed) {
                params.detailed = true;
            }
            if (filters?.page !== undefined) {
                params.page = filters.page;
            }
            if (filters?.limit !== undefined) {
                params.limit = filters.limit;
            }
            const queryString = BuildQueryString<RaainApiRainsCumulativesListRequest>(params);

            if (filters?.detailed) {
                const response = await this.fidjService.sendOnEndpoint<
                    void,
                    RaainApiRainsCumulativesDetailedResponse
                >({
                    key: 'rains',
                    verb: 'GET',
                    relativePath: rainId + '/cumulatives' + (queryString ? '?' + queryString : ''),
                    defaultKeyUrl: this.defaultUrlForAPI + '/rains',
                });
                return response.data;
            }

            const response = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsCumulativesListResponse
            >({
                key: 'rains',
                verb: 'GET',
                relativePath: rainId + '/cumulatives' + (queryString ? '?' + queryString : ''),
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });

            return response.data;
        } catch (e) {
            await this.checkError(e);
        }
        return {periods: [], total: 0};
    }

    // POST /rains/:rainId/cumulatives - Trigger cumulative computation
    async createCumulativePeriod(
        rainId: string,
        params: {
            periodBegin: Date;
            periodEnd: Date;
            provider: string;
            confName?: string;
            timeStepInMinutes?: number;
        }
    ): Promise<RaainApiRainsCumulativesCreateResponse> {
        try {
            const body: RaainApiRainsCumulativesCreateRequest = {
                periodBegin: params.periodBegin.toISOString(),
                periodEnd: params.periodEnd.toISOString(),
                provider: params.provider,
                confName: params.confName || 'admin',
                timeStepInMinutes: params.timeStepInMinutes || 5,
            };

            const response = await this.fidjService.sendOnEndpoint<
                RaainApiRainsCumulativesCreateRequest,
                RaainApiRainsCumulativesCreateResponse
            >({
                key: 'rains',
                verb: 'POST',
                relativePath: rainId + '/cumulatives',
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
                data: body,
            });

            return response.data;
        } catch (e) {
            await this.checkError(e);
        }
        return null;
    }

    // === Gauges ===

    async getGauge(gaugeId: string): Promise<GaugeNode> {
        try {
            const resp = await this.fidjService.sendOnEndpoint<void, RaainApiGaugesFindOneResponse>(
                {
                    key: 'gauges',
                    verb: 'GET',
                    relativePath: gaugeId,
                    defaultKeyUrl: this.defaultUrlForAPI + '/gauges',
                }
            );
            return new GaugeNode(resp.data);
        } catch (e) {
            await this.checkError(e);
        }
    }

    async getGauges(
        rainId: string,
        aroundLatLng: LatLng,
        pageCount = 1
    ): Promise<Array<GaugeNodeFilter>> {
        const baseParams: RaainApiGaugesFindAllMineRequest = {
            aroundLatLng: `${aroundLatLng.lat},${aroundLatLng.lng}`,
            rainId,
        };
        if (this.asTeamId) {
            baseParams.teamId = this.asTeamId;
        }

        const gauges = [];
        try {
            for (let count = 1; count <= pageCount; count++) {
                const params: RaainApiGaugesFindAllMineRequest = {...baseParams, page: count};
                const queryString = BuildQueryString<RaainApiGaugesFindAllMineRequest>(params);
                const resp = await this.fidjService.sendOnEndpoint<
                    void,
                    RaainApiGaugesFindAllMineResponse
                >({
                    key: 'gauges',
                    verb: 'GET',
                    relativePath: queryString ? '?' + queryString : '',
                    defaultKeyUrl: this.defaultUrlForAPI + '/gauges',
                });

                for (const gauge of resp.data.gauges) {
                    gauges.push(new GaugeNodeFilter(gauge));
                }
            }
        } catch (e) {
            await this.checkError(e);
        }
        return gauges;
    }

    async getGaugeMeasures(gaugeId: string, begin: Date, end: Date): Promise<GaugeMeasure[]> {
        const params: RaainApiGaugesFindMeasuresRequest = {
            begin: begin.toISOString(),
            end: end.toISOString(),
        };
        const queryString = BuildQueryString<RaainApiGaugesFindMeasuresRequest>(params);
        const resp = await this.fidjService.sendOnEndpoint<
            void,
            RaainApiGaugesFindMeasuresResponse
        >({
            key: 'gauges',
            verb: 'GET',
            relativePath: gaugeId + '/measures' + (queryString ? '?' + queryString : ''),
            defaultKeyUrl: this.defaultUrlForAPI + '/gauges',
        });

        const gaugeMeasures: GaugeMeasure[] = [];
        for (const gaugeMeasure of resp.data.gaugeMeasures) {
            gaugeMeasures.push(new GaugeMeasure(gaugeMeasure));
        }
        return gaugeMeasures;
    }

    async getProviders(
        rainId: string
    ): Promise<{providers: string[]; timeStepInMinutes: number[]}> {
        try {
            const response = await this.fidjService.sendOnEndpoint<
                void,
                RaainApiRainsProvidersResponse
            >({
                verb: 'GET',
                key: 'rains',
                relativePath: rainId + '/providers',
                defaultKeyUrl: this.defaultUrlForAPI + '/rains',
            });

            return {
                providers: response.data.providers || [],
                timeStepInMinutes: response.data.timeStepInMinutes || [5, 10, 15, 30, 60],
            };
        } catch (e) {
            console.error('getProviders error:', e);
            return {
                providers: [],
                timeStepInMinutes: [5, 10, 15, 30, 60],
            };
        }
    }

    protected setRoles(roles: Array<string>): void {
        this.roles = roles;
    }

    private setDateComponents(
        date: Date,
        c: {
            year?: number;
            month?: number;
            day?: number;
            hour?: number;
            minute?: number;
        }
    ): string {
        const dateToShow = new Date(date);

        if (c.year !== undefined) {
            dateToShow.setUTCFullYear(c.year);
        }
        if (c.month !== undefined) {
            dateToShow.setUTCMonth(c.month - 1);
        }
        if (c.day !== undefined) {
            dateToShow.setUTCDate(c.day);
        }
        if (c.hour !== undefined) {
            dateToShow.setUTCHours(c.hour);
        }
        if (c.minute !== undefined) {
            dateToShow.setUTCMinutes(c.minute);
        }

        return dateToShow.toISOString();
    }
}
